import { useEffect, useState, useMemo, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { fetchData } from "../../redux/slices/mainDataSlice";
import { toast } from "react-hot-toast";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faSync,
  faCheckCircle,
  faSchool,
  faRefresh,
} from "@fortawesome/free-solid-svg-icons";
import "../../main.scss";
import Can from "../../permissions/Can";

export default function SchoolRenewal() {
  const dispatch = useDispatch();
  const schools = useSelector((state) => state.main.data?.schoolData || [], (a, b) => JSON.stringify(a) === JSON.stringify(b));
  const renewalChoices = useSelector((state) => state.main.data?.renewalChoices || [], (a, b) => JSON.stringify(a) === JSON.stringify(b));
  const loading = useSelector((state) => state.main.loading);

  const [searchQuery, setSearchQuery] = useState("");
  const [isUpdating, setIsUpdating] = useState(false);
  const [isCheckingClosures, setIsCheckingClosures] = useState(false);
  const [closureResult, setClosureResult] = useState(null);

  useEffect(() => {
    dispatch(fetchData());
  }, [dispatch]);

  const getSchoolName = useCallback(
    (schoolNumber) => {
      const school = schools.find((s) => String(s.rgmw) === String(schoolNumber));
      return school?.schoolname || schoolNumber;
    },
    [schools]
  );

  const isSchoolOn = useCallback(
    (schoolNumber) => {
      const school = schools.find((s) => String(s.rgmw) === String(schoolNumber));
      return school?.onoff === "on";
    },
    [schools]
  );

  const filteredRenewalChoices = useMemo(() => {
    if (!searchQuery.trim()) return renewalChoices;
    const lowerSearch = searchQuery.toLowerCase();
    return renewalChoices.filter((choice) => {
      const schoolName = getSchoolName(choice.school_number);
      return (
        schoolName?.toLowerCase().includes(lowerSearch) ||
        String(choice.school_number).includes(lowerSearch)
      );
    });
  }, [renewalChoices, searchQuery, getSchoolName]);

  const handleUpdateCycleExpiry = async () => {
    if (isUpdating) return;

    setIsUpdating(true);
    try {
      const credentials = JSON.parse(localStorage.getItem("userCredentials"));
      const token = credentials?.token;

      const response = await fetch(
        `https://api.alfares.cloud/api/alfares/update-renewal-cycle-expiry/${token}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
        }
      );

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.message || "فشل في التحديث");
      }

      const data = await response.json();
      toast.success(data.message || "تم التحديث بنجاح");
      dispatch(fetchData(true));
    } catch (error) {
      toast.error(error.message || "فشل في التحديث");
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCheckClosures = async () => {
    if (isCheckingClosures) return;

    setIsCheckingClosures(true);
    setClosureResult(null);
    try {
      const credentials = JSON.parse(localStorage.getItem("userCredentials"));
      const token = credentials?.token;

      const response = await fetch(
        `https://api.alfares.cloud/api/alfares/schools/check-closure/${token}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
        }
      );

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.message || "فشل في فحص الإغلاق");
      }

      const data = await response.json();
      setClosureResult(data);
      toast.success(`تم فحص الإغلاق بنجاح - تم إغلاق ${data.updatedCount} مدرسة`);
      dispatch(fetchData(true));
    } catch (error) {
      toast.error(error.message || "فشل في فحص الإغلاق");
    } finally {
      setIsCheckingClosures(false);
    }
  };

  return (
    <div className="transferred-wrapper anim-fade-in">
      <div className="premium-edit-container" style={{ maxWidth: "1280px" }}>
        {/* Premium Compact Header */}
        <div className="premium-header-modern">
          <div className="header-left">
            <div className="header-icon-box renewal-icon">
              <FontAwesomeIcon icon={faSync} />
            </div>
            <div className="header-text-box">
              <h2>الراغبة في التجديد</h2>
              <div className="breadcrumb-info">
                <span>نظام الفارس</span>
                <span className="separator">/</span>
                <span className="current-page">إدارة المدارس</span>
                <span className="separator">/</span>
                <span className="current-page">الراغبة في التجديد</span>
              </div>
            </div>
          </div>

          <div className="header-tools">
            <div className="global-search-container">
              <FontAwesomeIcon icon={faSearch} className="s-icon" />
              <input
                type="text"
                placeholder="ابحث عن مدرسة..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            <Can module="schools" action="edit">
            <button
              className="refresh-btn"
              onClick={handleUpdateCycleExpiry}
              disabled={isUpdating}
            >
              <FontAwesomeIcon icon={faRefresh} className={isUpdating ? "rotating" : ""} />
              <span>{isUpdating ? "جارٍ التحديث..." : "تحديث"}</span>
            </button>
            </Can>

            <Can module="schools" action="edit">
            <button
              className="refresh-btn"
              onClick={handleCheckClosures}
              disabled={isCheckingClosures}
              style={{ background: "linear-gradient(135deg, #f59e0b, #d97706)" }}
            >
              <FontAwesomeIcon icon={faCheckCircle} className={isCheckingClosures ? "rotating" : ""} />
              <span>{isCheckingClosures ? "جارٍ الفحص..." : "فحص الاغلاق"}</span>
            </button>
            </Can>

            <div className="results-count">
              <b>{filteredRenewalChoices.length}</b> مدرسة
            </div>
          </div>
        </div>

        {/* Table Content */}
        <div className="premium-table-container">
          {loading ? (
            <div className="loader-overlay">
              <div className="premium-loader"></div>
              <span>جارٍ جلب البيانات...</span>
            </div>
          ) : (
            <table className="modern-condensed-table">
              <thead>
                <tr>
                  <th width="50" className="idx-header">
                    #
                  </th>
                  <th className="school-header">المدرسة</th>
                  <th className="choice-header">خيار التجديد</th>
                  <th className="date-header">تاريخ الاختيار</th>
                  <th className="date-header">تاريخ انتهاء الدورة</th>
                </tr>
              </thead>
              <tbody>
                {filteredRenewalChoices.length > 0 ? (
                  filteredRenewalChoices.map((choice, idx) => (
                    <tr key={choice.id || idx}>
                      <td
                        className="idx-cell"
                        style={{
                          color: "var(--muted)",
                          fontWeight: 400,
                          textAlign: "center",
                        }}
                      >
                        {idx + 1}
                      </td>
                      <td className="info-cell">
                        <div className="meta-minimal">
                          <span className="name">
                            {getSchoolName(choice.school_number)}
                          </span>
                          <p className="school-number">
                            رقم المدرسة: {choice.school_number}
                          </p>
                        </div>
                      </td>
                      <td className="choice-cell">
                        <span
                          className="status-dot"
                          title={isSchoolOn(choice.school_number) ? "المدرسة عاملة" : "المدرسة متوقفة"}
                          style={{
                            backgroundColor: isSchoolOn(choice.school_number) ? "#22c55e" : "#ef4444",
                          }}
                        />
                      </td>
                      <td className="date-col" data-label="تاريخ الاختيار">
                        <div className="type-badge date-badge">
                          {choice.choice_date || "-"}
                        </div>
                      </td>
                      <td className="date-col" data-label="انتهاء الدورة">
                        <div className="type-badge date-badge">
                          {choice.is_paid ? "مدفوع" : choice.cycle_expiry || "-"}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="empty-ui-state">
                      <FontAwesomeIcon icon={faSchool} className="icon" />
                      <h3>لا توجد مدارس راغبة في التجديد</h3>
                      <p>لم يتم العثور على بيانات مطابقة</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Closure Result Modal */}
      {closureResult && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
          onClick={() => setClosureResult(null)}
        >
          <div
            style={{
              background: "var(--panel-2)",
              borderRadius: "6px",
              padding: "24px",
              maxWidth: "500px",
              width: "90%",
              maxHeight: "80vh",
              overflow: "auto",
              border: "1px solid var(--border)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: "0 0 16px", color: "var(--text)" }}>
              نتائج فحص الإغلاق
            </h3>
            <p style={{ margin: "0 0 16px", color: "var(--muted)" }}>
              تم إغلاق <strong>{closureResult.updatedCount}</strong> مدرسة
            </p>
            {closureResult.updatedSchools && closureResult.updatedSchools.length > 0 ? (
              <div
                style={{
                  maxHeight: "300px",
                  overflow: "auto",
                  background: "var(--bg)",
                  borderRadius: "8px",
                  padding: "12px",
                }}
              >
                {closureResult.updatedSchools.map((school, idx) => (
                  <div
                    key={idx}
                    style={{
                      padding: "8px",
                      borderBottom: "1px solid var(--border)",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <span style={{ color: "var(--text)" }}>{school.schoolname}</span>
                    <span style={{ color: "var(--muted)", fontSize: "12px" }}>
                      {school.rgmw}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p style={{ color: "var(--muted)" }}>لا توجد مدارس تم إغلاقها</p>
            )}
            <button
              onClick={() => setClosureResult(null)}
              style={{
                marginTop: "16px",
                padding: "10px 20px",
                background: "linear-gradient(135deg, #28917f, #1d7567)",
                color: "white",
                border: "none",
                borderRadius: "8px",
                cursor: "pointer",
                width: "100%",
              }}
            >
              إغلاق
            </button>
          </div>
        </div>
      )}

      <style>{`
        .transferred-wrapper {
          padding: 5px;
          color: var(--text);
          font-family: 'Cairo', sans-serif;
          font-weight: 400;
        }

        .premium-edit-container {
          background: transparent;
          border: none;
          border-radius: 0;
          margin: 0;
          overflow: visible;
          box-shadow: none;
          width: 100%;
          position: relative;
        }

        .premium-header-modern {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px 30px;
          background: rgba(255, 255, 255, 0.7);
          backdrop-filter: blur(10px);
          border: 1px solid var(--border);
          border-radius: 6px 6px 0 0;
          gap: 20px;
        }

        .premium-table-container {
          padding: 15px;
          background: rgba(255, 255, 255, 0.5);
          backdrop-filter: blur(10px);
          border: 1px solid var(--border);
          border-top: none;
          border-radius: 0 0 6px 6px;
        }

        .header-icon-box {
          width: 48px; height: 48px;
          background: var(--accent);
          border-radius: 14px;
          display: flex; align-items: center; justify-content: center;
          font-size: 20px; color: white;
        }
        .header-icon-box.renewal-icon { background: linear-gradient(135deg, #28917f, #1d7567); }

        .header-text-box h2 { margin: 0; font-size: 20px; font-weight: 400; }
        .breadcrumb-info { display: flex; align-items: center; gap: 4px; color: var(--muted); font-size: 12px; margin-top: 2px; }
        .breadcrumb-info span:first-child { color: #28917f; font-weight: 400; }
        .current-page { color: #1d7567; font-weight: 400; }
        .separator { color: var(--muted); }

        .header-tools { display: flex; align-items: center; gap: 10px; flex: 1; justify-content: flex-end; }

        .global-search-container {
          background: var(--bg);
          border: 1px solid var(--border);
          border-radius: 12px;
          padding: 0 15px;
          display: flex;
          align-items: center;
          gap: 12px;
          width: 100%;
          max-width: 300px;
          height: 44px;
          transition: all 0.3s;
        }
        .global-search-container:focus-within { border-color: #28917f; box-shadow: 0 0 0 3px rgba(40, 145, 127, 0.1); }
        .s-icon { color: #28917f; }
        .global-search-container input { background: transparent; border: none; color: var(--text); font-size: 14px; width: 100%; outline: none; font-family: 'Cairo', sans-serif; font-weight: 400; }

        .results-count {
          background: var(--panel-2);
          padding: 0 15px;
          border-radius: 12px;
          font-size: 13px;
          color: var(--muted);
          border: 1px solid var(--border);
          display: flex;
          align-items: center;
          gap: 6px;
          height: 44px;
          white-space: nowrap;
          transition: all 0.3s;
          font-family: 'Cairo', sans-serif;
          font-weight: 400;
        }
        .results-count:hover { border-color: #28917f; background: rgba(40, 145, 127, 0.05); }
        .results-count b { color: #28917f; font-size: 15px; font-weight: 400; }

        .refresh-btn {
          background: linear-gradient(135deg, #28917f, #1d7567);
          color: white;
          border: none;
          border-radius: 12px;
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 13px;
          font-weight: 400;
          cursor: pointer;
          padding: 10px 18px;
          transition: all 0.3s;
          white-space: nowrap;
          font-family: 'Cairo', sans-serif;
        }
        .refresh-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(40, 145, 127, 0.3);
        }
        .refresh-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
        }
        .refresh-btn .rotating {
          animation: spin 1s infinite linear;
        }

        .premium-table-container { padding: 15px 20px 20px; }

        .modern-condensed-table {
          width: 100%;
          border-collapse: collapse;
          text-align: right;
          font-size: 13px;
          table-layout: auto;
          direction: rtl;
          font-family: 'Cairo', sans-serif;
          font-weight: 400;
        }
        .modern-condensed-table th {
          background: var(--panel-2);
          padding: 10px 12px;
          color: var(--accent);
          font-weight: 400;
          border-bottom: 1px solid var(--border);
          white-space: nowrap;
        }
        .modern-condensed-table td {
          padding: 8px 12px;
          border-bottom: 1px solid rgba(255, 255, 255, 0.05);
          color: var(--text);
          white-space: nowrap;
          font-weight: 400;
        }
        .modern-condensed-table tr:hover {
          background: rgba(255, 255, 255, 0.02);
        }

        .info-cell {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .meta-minimal { width: 100%; }
        .meta-minimal .name {
          font-weight: 400;
          font-size: 15px;
          color: var(--text);
        }
        .school-number {
          margin: 4px 0 0 0;
          font-size: 12px;
          color: var(--muted);
        }

        .choice-badge {
          display: inline-block;
          padding: 6px 12px;
          border-radius: 8px;
          background: rgba(40, 145, 127, 0.1);
          color: #28917f;
          font-size: 13px;
          font-weight: 400;
        }
        .choice-badge.paid {
          background: rgba(16, 185, 129, 0.1);
          color: #10b981;
        }

        .status-dot {
          display: inline-block;
          width: 16px;
          height: 16px;
          border-radius: 50%;
          box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.06);
        }

        .date-badge {
          font-size: 12px;
          white-space: nowrap;
          padding: 4px 8px;
          border-radius: 6px;
          background: var(--panel);
          border: 1px solid var(--border);
        }

        .status-badge {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 4px 10px;
          border-radius: 8px;
          font-size: 13px;
          font-weight: 400;
        }
        .status-badge .status-icon {
          font-size: 14px;
        }
        .status-badge .status-text {
          font-weight: 400;
        }
        .status-badge[style*="#10b981"] {
          background: rgba(16, 185, 129, 0.1);
          color: #10b981;
        }
        .status-badge[style*="#f59e0b"] {
          background: rgba(245, 158, 11, 0.1);
          color: #f59e0b;
        }

        .premium-loader { width: 30px; height: 30px; border: 2px solid rgba(40, 145, 127, 0.1); border-top-color: #28917f; border-radius: 50%; animation: spin 1s infinite linear; margin: 0 auto 10px; }
        @keyframes spin { to { transform: rotate(360deg); } }

        .loader-overlay {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 60px 20px;
          color: var(--muted);
        }

        .empty-ui-state {
          text-align: center;
          padding: 60px 20px;
          color: var(--muted);
        }
        .empty-ui-state .icon {
          font-size: 48px;
          margin-bottom: 16px;
          opacity: 0.5;
        }
        .empty-ui-state h3 {
          margin: 0 0 8px;
          font-size: 18px;
          font-weight: 400;
        }
        .empty-ui-state p {
          margin: 0;
          font-size: 14px;
        }

        @media (max-width: 1000px) {
          .premium-header-modern { flex-direction: column; align-items: stretch; padding: 20px; }
          .header-left { justify-content: center; margin-bottom: 15px; }
          .header-tools { flex-direction: column; width: 100%; }
          .global-search-container { max-width: 100%; }
        }

        @media (max-width: 768px) {
          .global-search-container, .results-count { display: none !important; }

          /* Mobile header: stacked, buttons side-by-side */
          .premium-header-modern {
            flex-direction: column !important;
            align-items: stretch !important;
            padding: 14px 12px !important;
            gap: 12px !important;
            border-radius: 6px 6px 0 0 !important;
          }
          .header-left { margin-bottom: 0 !important; }
          .header-text-box h2 { font-size: 16px !important; }
          .breadcrumb-info { font-size: 11px !important; flex-wrap: wrap; }
          .header-tools {
            display: grid !important;
            grid-template-columns: 1fr 1fr !important;
            gap: 8px !important;
            width: 100% !important;
            flex: none !important;
          }
          .refresh-btn {
            justify-content: center !important;
            padding: 12px 8px !important;
            font-size: 12px !important;
            border-radius: 6px !important;
            min-height: 44px;
          }

          .premium-table-container { padding: 10px !important; border-radius: 0 0 6px 6px !important; }

          /* Mobile cards: clean grid, no overlap */
          .modern-condensed-table thead { display: none !important; }
          .modern-condensed-table { display: block !important; width: 100% !important; }
          .modern-condensed-table tbody {
            display: flex !important;
            flex-direction: column;
            gap: 10px;
            width: 100% !important;
          }
          .modern-condensed-table tr {
            display: grid !important;
            grid-template-columns: 1fr auto;
            grid-template-areas:
              "info choice"
              "info idx"
              "date1 date2";
            gap: 8px 10px;
            align-items: center;
            padding: 12px;
            background: var(--panel-2);
            border-radius: 6px;
            border: 1px solid var(--border) !important;
            width: 100% !important;
            box-sizing: border-box;
          }
          .modern-condensed-table td {
            display: block !important;
            border: none !important;
            padding: 0 !important;
            min-width: 0;
            white-space: normal !important;
          }

          .info-cell { grid-area: info; }
          .meta-minimal .name { font-size: 14px !important; }
          .school-number { font-size: 11px !important; }
          .choice-cell { grid-area: choice; justify-self: end; align-self: start; }
          .choice-badge { font-size: 12px !important; white-space: nowrap; }
          .idx-cell {
            grid-area: idx; justify-self: end;
            font-size: 11px !important; color: var(--muted);
            background: var(--bg); border-radius: 6px;
            padding: 2px 10px !important;
            margin: 0 !important;
          }
          .modern-condensed-table tr > td:nth-child(4) { grid-area: date1; }
          .modern-condensed-table tr > td:nth-child(5) { grid-area: date2; }
          .date-col .date-badge {
            display: block; text-align: center;
            font-size: 11px !important; padding: 6px 4px !important;
          }
          .date-col::before {
            content: attr(data-label);
            display: block;
            font-size: 10px;
            color: var(--muted);
            margin-bottom: 3px;
            text-align: center;
          }
        }
      `}</style>
    </div>
  );
}
